import { defineStore } from "pinia";
import { useTabsStore } from "./tabs.store";
import { _deepClone, _sleep } from "nvd-js-helpers/misc";
import { FetchRequest } from "../helpers/fetch-request";
import { useNotify } from "nvd-u/composables/Notifiy";
import { dayjs } from "nvd-js-helpers/dayjs";
import { useSettingsStore } from "./settings.store";
import { SchemaToDbml } from "../components/code-editor/schemaToDbml";
import { assistantStorageKey } from "@/components/code-editor/assistant-history";
import { useAuthStore } from "./auth.store";

function getAiAssistantRequestIds(storageKey) {
    try {
        const raw = localStorage.getItem(storageKey)
        const parsed = raw ? JSON.parse(raw) : []
        return Array.isArray(parsed)
            ? parsed.map(message => message?.request_id).filter(Boolean)
            : []
    } catch (e) {
        return []
    }
}

function clearLocalAiAssistantState(storageKey) {
    try {
        const staleKey = `${storageKey}-stale-request-ids`
        const existingRaw = localStorage.getItem(staleKey)
        const existing = existingRaw ? JSON.parse(existingRaw) : []
        const activeRequestId = localStorage.getItem(`${storageKey}-active-request`)
        const staleRequestIds = Array.from(new Set([
            ...(Array.isArray(existing) ? existing : []),
            ...getAiAssistantRequestIds(storageKey),
            ...(activeRequestId ? [activeRequestId] : [])
        ]))

        if (staleRequestIds.length) {
            localStorage.setItem(staleKey, JSON.stringify(staleRequestIds))
        }
        localStorage.removeItem(storageKey)
        localStorage.removeItem(`${storageKey}-thread-id`)
        localStorage.removeItem(`${storageKey}-active-request`)
    } catch (e) {
        // ignore storage errors
    }
}

function dispatchVersionPreviewClosed(tab) {
    if (typeof window === 'undefined' || !tab) return
    window.dispatchEvent(new CustomEvent('schema-version-preview-closed', {
        detail: {
            schemaId: tab.schema?.id,
            schemaSlug: tab.schema?.slug,
            closedAt: Date.now()
        }
    }))
}

export const useVersionsStore = defineStore('versions', {
    state: () => ({
        versions: new FetchRequest('', 'POST'),
        selectedVersion: null,
        originalSchema: null,
        expanded: {},
        isPreviewing: false,
        previewReq: new FetchRequest('', 'GET'),
        restoreReq: new FetchRequest('', 'POST')
    }),
    getters: {},
    actions: {
        load() {
            const tab = useTabsStore()
            if (!tab.selectedTab.schema.id) return
            this.versions.url = `schemas/${tab.selectedTab.schema.slug}/versions`
            return this.versions.send()
        },
        async reset(tabId = null) {
            const previewReq = this.previewReq
            const tab = useTabsStore()
            let selectedTab = tab.selectedTab
            const wasPreviewing = this.isPreviewing || !!this.selectedVersion || !!this.originalSchema
            if (tabId) {
                let oldTab = tab.tabs.find(t => t.id === tabId)
                if (oldTab) selectedTab = oldTab
            }
            if (this.originalSchema) {
                selectedTab.schema.schema_data = this.originalSchema
                this.originalSchema = null
            }
            this.selectedVersion = null
            this.expanded = {}
            await _sleep(600)
            if (this.previewReq !== previewReq) return
            this.isPreviewing = false
            if (wasPreviewing) dispatchVersionPreviewClosed(selectedTab)
        },
        preview(version) {
            const tab = useTabsStore()
            this.previewReq.url = `schemas/${tab.selectedTab.schema.slug}/show_version?v_id=${version.id}`
            if (!this.selectedVersion) {
                this.originalSchema = tab.selectedTab.schema.schema_data
            }
            this.selectedVersion = version

            // prevent unnecessary http request
            if (version.schema_data) {
                this.setPreview(version)
                return
            }

            const previewReq = this.previewReq
            const selectedVersion = this.selectedVersion
            previewReq.send().then(data => {
                if (this.previewReq !== previewReq || this.selectedVersion !== selectedVersion) return
                return this.setPreview(data)
            })
        },
        async setPreview({ schema_data }) {
            const tab = useTabsStore()
            this.selectedVersion.schema_data = typeof schema_data === 'string' ? JSON.parse(schema_data) : schema_data
            this.isPreviewing = true
            tab.selectedTab.schema.schema_data = typeof schema_data === 'string' ? JSON.parse(schema_data) : schema_data
            new SchemaToDbml(tab.selectedTab).convert()
        },
        closePreview() {
            const tab = useTabsStore()
            const selectedTab = tab.selectedTab
            const wasPreviewing = this.isPreviewing || !!this.selectedVersion || !!this.originalSchema
            this.selectedVersion = null
            this.originalSchema = null
            this.isPreviewing = false
            if (wasPreviewing) dispatchVersionPreviewClosed(selectedTab)
        },
        restoreToVersion() {
            if (!this.selectedVersion?.id) return
            return this.restoreVersionById(this.selectedVersion.id, this.selectedVersion.created_at)
        },
        restoreVersionById(versionId, createdAt = null) {
            const tab = useTabsStore()
            const notify = useNotify()
            const settings = useSettingsStore()
            this.restoreReq.url = `schemas/${tab.selectedTab.schema.slug}/restore_version`
            this.restoreReq.config.body = JSON.stringify({
                v_id: versionId
            })
            this.closePreview()
            settings.modals.history = false

            const restoreReq = this.restoreReq
            return restoreReq.send()
                .then((data) => {
                    if (this.restoreReq !== restoreReq) return
                    const formattedDate = createdAt ? dayjs.unix(createdAt).format('MMM D, YYYY hh:mm a') : null
                    notify.success(formattedDate ? `Restored from ${formattedDate}` : 'Version restored')
                    // update local schema here if needed
                    let selectedTab = tab.tabs.find(t =>
                        t.id === data.id
                        || t.schema?.id === data.id
                        || (data.slug && t.schema?.slug === data.slug)
                    )
                    if (!selectedTab) return
                    const restoredSchemaData = _deepClone(data.schema_data)
                    Object.assign(selectedTab.schema, data, {
                        schema_data: restoredSchemaData
                    })
                    new SchemaToDbml(selectedTab).convert()
                    const aiStorageKey = assistantStorageKey(selectedTab.schema, useAuthStore().user?.id)
                    clearLocalAiAssistantState(aiStorageKey)
                    window.dispatchEvent(new CustomEvent('schema-version-restored', {
                        detail: {
                            schemaId: selectedTab.schema?.id,
                            schemaSlug: selectedTab.schema?.slug,
                            storageKey: aiStorageKey,
                            restoredAt: Date.now()
                        }
                    }))
                    return data
                })
        }
    }
})
